import React, { useEffect, useState } from 'react';
import { getHistoryMessage } from '../../api/system/system.api';

/**
 * notification 은 버튼 주소를 `https://link.omoplay.com/redirect?version=2&redirectUrl=<base64url>` 로 감싸서 보낸다.
 * 감싼 주소만 보면 실제로 어디로 가는지 알 수 없으므로 **원래 주소를 풀어서** 함께 보여준다.
 * 감싸지 않은 주소(useRawUrl)·풀 수 없는 값이면 null.
 */
export const unwrapRedirectUrl = (url) => {
  try {
    const u = new URL(url);
    if (u.hostname !== 'link.omoplay.com' || !u.pathname.startsWith('/redirect')) return null;
    const raw = u.searchParams.get('redirectUrl');
    if (!raw) return null;
    if (u.searchParams.get('version') !== '2') return raw; // 구버전은 평문
    const b64 = raw.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    return decodeURIComponent(escape(atob(padded)));
  } catch {
    return null;
  }
};

const Line = ({ label, children }) => (
  <tr>
    <td className="px-2 py-0.5 text-gray-400 align-top whitespace-nowrap">{label}</td>
    <td className="px-2 py-0.5 text-gray-700 break-all">{children}</td>
  </tr>
);

const ButtonLine = ({ b }) => {
  const url = b.url_mobile ?? b.urlMobile ?? b.url_pc ?? b.urlPc ?? null;
  const original = url ? unwrapRedirectUrl(url) : null;
  return (
    <li className="py-0.5">
      <b>{b.name ?? '(이름 없음)'}</b> <span className="text-gray-400">{b.type}</span>
      {url && <div className="font-mono text-[11px] text-gray-500 break-all">{url}</div>}
      {original && <div className="font-mono text-[11px] text-indigo-700 break-all">→ 실제 이동: {original}</div>}
    </li>
  );
};

/**
 * 이력의 메시지 행 → **실제 보낸 내용**(2026-10-20). RevenueOS 시도별 '보낸 내용 보기' 와 같은 역할.
 *
 * - 원천 = `message_send_detail`(본문 전체·제목·SureM 요청 원문) + `kakao_send_history`(결과). 서버가 합쳐 준다.
 *   상세가 없는 과거 발송(이관 전)은 ksh `content` 로 본문을 보여주고 그렇다고 표시한다.
 * - 버튼·요청 원문은 notification 2026-10-20 배포 **이후** 발송부터 남는다 — 그 전 발송은 '기록 없음' 이 정상이다.
 * - 수신번호는 서버가 가려서 준다(요청 원문의 `to` 포함). 본문에는 카드 등록 링크 같은 실제 링크가 그대로 있다(관리자 화면 전용).
 */
const HistoryMessageView = ({ channelId, historyId }) => {
  const [view, setView] = useState(undefined);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    getHistoryMessage(channelId, historyId)
      .then((res) => { if (alive) setView(res?.data ?? null); })
      .catch((e) => { if (alive) setErr(e?.message ?? '내용을 불러오지 못했습니다.'); });
    return () => { alive = false; };
  }, [channelId, historyId]);

  if (err) return <div className="mt-1 rounded border border-amber-200 bg-amber-50 p-2 text-amber-700">내용을 불러오지 못했습니다: {err}</div>;
  if (view === undefined) return <div className="mt-1 text-gray-400">내용을 불러오는 중…</div>;
  if (view === null) return <div className="mt-1 text-gray-400">이 행에는 볼 수 있는 메시지가 없습니다.</div>;

  return (
    <div className="mt-1 rounded border border-gray-200 bg-white p-2">
      {!view.matchedBy && (
        <div className="mb-1 text-amber-700">발송 원장에서 이 메시지를 찾지 못했습니다(구버전 기록이거나 보관 기간이 지났을 수 있음).</div>
      )}
      <table>
        <tbody>
          <Line label="목적">{view.purposeLabel ?? view.purpose ?? '—'} · {view.channel ?? '—'}{view.templateCode ? ` · ${view.templateCode}` : ''}</Line>
          {view.kind && <Line label="구조"><span className="font-mono">{view.kind}</span></Line>}
          <Line label="받는 번호"><span className="font-mono">{view.phoneMasked ?? '—'}</span></Line>
          <Line label="결과">
            {view.resultLabel}
            {view.errorCode && <span className="ml-1 font-mono text-red-600">({view.errorCode})</span>}
            {view.submitCode && <span className="ml-1 text-gray-400">· 접수 {view.submitCode}</span>}
          </Line>
          <Line label="시각">발송 {view.sendAt ?? '—'}{view.recvTime ? ` · 결과 수신 ${view.recvTime}` : ''}</Line>
          {view.sendTrigger && <Line label="발송 사유"><span className="font-mono">{view.sendTrigger}</span></Line>}
          {view.subject && <Line label="제목">{view.subject}</Line>}
        </tbody>
      </table>
      {view.content && (
        <div className="mt-1 rounded bg-gray-50 border border-gray-200 p-2 whitespace-pre-wrap text-gray-700">{view.content}</div>
      )}
      {view.contentSource === 'ksh' && (
        <div className="mt-0.5 text-[11px] text-gray-400">상세 기록이 없는 과거 발송이라 발송 원장의 본문을 보여줍니다(와이드 리스트·캐러셀은 구분 없이 이어 붙은 값일 수 있음).</div>
      )}
      <div className="mt-1">
        <span className="text-gray-400">버튼</span>
        {view.buttons && view.buttons.length > 0 ? (
          <ul className="ml-2">{view.buttons.map((b, i) => <ButtonLine key={i} b={b} />)}</ul>
        ) : (
          <span className="ml-2 text-gray-400">기록 없음</span>
        )}
      </div>
      {view.payload && (
        <details className="mt-1">
          <summary className="cursor-pointer text-gray-500">
            요청 원문{view.payloadVersion === 0 ? '(과거 이관 · original_data)' : ''}
          </summary>
          <pre className="mt-1 rounded bg-gray-50 border border-gray-200 p-2 text-[11px] text-gray-600 whitespace-pre-wrap break-all">
            {JSON.stringify(view.payload, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
};

export default HistoryMessageView;
